import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { AccessibleOfficeDto } from '@gh/builder-shared-data';
import { TranslationService } from '@gh/core-mls';
import { DialogInput, STYLES_DIALOG_LAYOUT } from '@gh/core-ui';

export type SelectOfficeDialogConfig = {
  offices: AccessibleOfficeDto[];
};

/** Up to this many offices are shown as cards; more switch the picker to a dropdown. */
export const MAX_OFFICE_CARDS = 3;

/**
 * Opens during auth bootstrap before MLS maps may be loaded.
 * Use resolveLabel() so we never show raw keys like msg_select_office_to_continue.
 */
@Component({
  standalone: false,
  selector: 'gh-select-office-dialog',
  templateUrl: 'select-office-dialog.component.html',
  styleUrls: ['select-office-dialog.component.scss'],
  styles: [STYLES_DIALOG_LAYOUT],
})
export class SelectOfficeDialogComponent implements DialogInput<SelectOfficeDialogConfig> {
  selectedOffice?: AccessibleOfficeDto;
  private _input: SelectOfficeDialogConfig;

  readonly title = this.resolveLabel('lbl_select_office', 'Select Office');
  readonly intro = this.resolveLabel(
    'msg_select_office_to_continue',
    'Choose the office you want to work in for this session.',
  );
  readonly officeLabel = this.resolveLabel('lbl_office', 'Office');
  readonly homeOfficeLabel = this.resolveLabel('lbl_primary_office', 'Primary Office');
  readonly continueLabel = this.resolveLabel('lbl_continue', 'Continue');
  readonly differentAccountLabel = this.resolveLabel('lbl_log_out', 'Log Out');

  constructor(
    private dialogRef: MatDialogRef<SelectOfficeDialogComponent>,
    private translation: TranslationService,
  ) {}

  set input(value: SelectOfficeDialogConfig) {
    this._input = value;
    if (!this.selectedOffice && value?.offices?.length) {
      this.selectedOffice = value.offices.find((office) => office.homeOffice) || value.offices[0];
    }
  }

  get input(): SelectOfficeDialogConfig {
    return this._input;
  }

  get offices(): AccessibleOfficeDto[] {
    return this._input?.offices || [];
  }

  get useDropdown(): boolean {
    return this.offices.length > MAX_OFFICE_CARDS;
  }

  compareOffices = (left: Maybe<AccessibleOfficeDto>, right: Maybe<AccessibleOfficeDto>): boolean =>
    Boolean(left && right) && String(left!.officeId) === String(right!.officeId);

  isSelected(office: AccessibleOfficeDto): boolean {
    const selectedId = this.selectedOffice?.officeId;
    return Boolean(selectedId) && String(selectedId) === String(office.officeId);
  }

  selectOffice(office: AccessibleOfficeDto): void {
    this.selectedOffice = office;
  }

  selectOfficeById(officeId: unknown): void {
    const office = this.offices.find((candidate) => String(candidate.officeId) === String(officeId));
    if (office) {
      this.selectOffice(office);
    }
  }

  onContinue(): void {
    if (!this.selectedOffice) {
      return;
    }
    this.dialogRef.close(this.selectedOffice);
  }

  /** Closing without an office signs the user out (OfficeAccessService), so they can sign in with another account. */
  onUseDifferentAccount(): void {
    this.dialogRef.close();
  }

  private resolveLabel(key: string, fallback: string): string {
    const translated = this.translation.get(key);
    return !translated || translated === key ? fallback : translated;
  }
}
